import { useState } from 'react';
import { Eraser, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import type { Subject } from './api';
import { startSubjectDrag } from './drag';
import { iconFor, readableOn } from './subjectStyle';

type SubjectPaletteProps = { subjects: Subject[] };

const STORAGE_KEY = 'rd-palette-open';

function readOpen(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

/** The subjects to drag onto the sheet, in their colours; collapsible, remembered per browser. */
export function SubjectPalette({ subjects }: SubjectPaletteProps) {
  const [open, setOpen] = useState(readOpen);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // Not remembered; still works.
    }
  };

  return (
    <aside aria-label="Vakken" className="no-print hidden shrink-0 md:flex md:flex-col md:gap-2">
      <Button variant="ghost" size="icon" aria-label={open ? 'Vakken verbergen' : 'Vakken tonen'} onClick={toggle} className="min-h-11 min-w-11 self-end">
        {open ? <PanelRightClose /> : <PanelRightOpen />}
      </Button>
      {open && (
        <div className="panel flex w-52 flex-col gap-2 p-3">
          {subjects.map((subject) => (
            <span
              key={subject.id}
              draggable
              onDragStart={(event) => startSubjectDrag(event, subject.id)}
              title="Sleep naar het rooster"
              className="flex min-h-11 cursor-grab items-center gap-2 rounded-md px-3 py-1 font-semibold leading-tight active:cursor-grabbing"
              style={{ background: subject.color, color: readableOn(subject.color) }}
            >
              <span aria-hidden="true" className="shrink-0">{iconFor(subject.icon)}</span>
              <span className="min-w-0 break-words hyphens-auto">{subject.name}</span>
            </span>
          ))}
          {subjects.length === 0 && (
            <p className="text-sm">
              Je hebt nog geen vakken.{' '}
              <Link to="/instellingen/vakken" className="underline">
                Voeg eerst vakken toe
              </Link>
              .
            </p>
          )}
          <span
            draggable
            onDragStart={(event) => startSubjectDrag(event, null)}
            title="Sleep naar een vakje om het te wissen"
            className="flex min-h-11 cursor-grab items-center gap-2 rounded-md border border-dashed px-3 active:cursor-grabbing"
          >
            <Eraser className="h-4 w-4" aria-hidden="true" /> Wissen
          </span>
          <p className="text-xs opacity-70">Tab: volgend vakje · Enter: klaar · Esc: herstelt</p>
        </div>
      )}
    </aside>
  );
}
